<template>
    <div>
        <sms-history-empty-state v-if="!setupComplete && !loading" />

        <div v-if="setupComplete">
            <div class="section-header marketing-messages-header">
                <div class="marketing-messages-info">
                    <span class="section-title"> Sent from your Keap Marketing Number </span>

                    <span class="phone-number">
                        {{ marketingNumber }}
                    </span>
                </div>
            </div>

            <div v-if="!hasMessageHistory && !loading" class="empty-state">
                This contact hasn't received any text messages from your Keap Marketing Number
            </div>

            <div v-if="!showHistoryInlineAlert" class="message-history">
                <message-history-item
                    v-for="message in sortedAutomatedMessageHistory"
                    :key="message.id"
                    :loading="loading"
                    :message="message"
                    :contact="contact"
                    :opt-status="getOptStatus(message)"
                    :source-title="getSourceTitle(message)"
                />

                <div v-if="loadingMessages" class="message-history-placeholder">
                    <messages-list-item-placeholder v-for="n in 5" :key="n" />
                </div>

                <ds-infinite-scroll
                    :limit="limit"
                    :loading="loadingMessages"
                    :no-more-data="noMoreData"
                    @load="handleInfiniteScrollLoad"
                />
            </div>

            <div v-else class="error-loading-box">
                <error-box class="error-retry" @retry="load">
                    There was a problem loading your text message history. This is likely the result of a system error.

                    <template #retry>
                        Reload your text messages
                    </template>
                </error-box>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

import { normalizePhoneNumber } from '@/components/broadcast/utils/sms-utils';
import { formatPhone } from '@/utils/phone.util';
import MessageHistoryItem from './MessageHistoryItem';
import SmsHistoryEmptyState from './SmsHistoryEmptyState';
import ErrorBox from '@/components/shared/ErrorBox';
import MessagesListItemPlaceholder from './MessagesListItemPlaceholder';
import { getRecipientPhoneStatus } from '@/api/broadcasts/smsBroadcasts';

const ADVANCED_AUTOMATION = 'ADVANCED_AUTOMATION';
const BROADCAST = 'BROADCAST';

export default {
    name: 'SmsMessageHistory',

    components: {
        MessageHistoryItem,
        MessagesListItemPlaceholder,
        SmsHistoryEmptyState,
        ErrorBox,
    },

    props: {
        contactId: {
            type: Number,
            default: 0,
        },
    },

    data() {
        return {
            automatedMessageHistory: [],
            showHistoryInlineAlert: false,
            loading: true,
            firstLoad: true,
            limit: 20,
            loadingMessages: true,
            noMoreData: false,
            offset: 0,
            contact: null,
            optStatuses: [],
            automatedSmsAccount: null,
            campaignSummaries: [],
            setupComplete: false,
        };
    },

    mounted() {
        this.load();
    },

    computed: {
        sortedAutomatedMessageHistory() {
            return [...this.automatedMessageHistory]
                .sort(({ updated: updated1 }, { updated: updated2 }) => {
                    const time1 = moment(updated1);
                    const time2 = moment(updated2);

                    if (time1.isBefore(time2)) return 1;
                    if (time2.isBefore(time1)) return -1;

                    return 0;
                });
        },

        phones() {
            if (!this.contact) return [];

            const {
                phone1,
                phone2,
                phone3,
                phone4,
                phone5,
            } = this.contact;

            return [
                phone1,
                phone2,
                phone3,
                phone4,
                phone5,
            ].filter((item) => item);
        },

        marketingNumber() {
            return formatPhone(this.automatedSmsAccount?.phoneNumber);
        },

        hasMessageHistory() {
            return this.automatedMessageHistory.length > 0;
        },
    },

    methods: {
        async load() {
            this.loading = true;

            try {
                await this.loadAutomatedSmsAccount();
            } catch (error) {
                this.showHistoryInlineAlert = true;
            }

            if (this.setupComplete) {
                try {
                    await Promise.all([
                        this.loadMessageHistory(),
                        this.loadContactDetails(),
                    ]);
                } catch (error) {
                    this.showHistoryInlineAlert = true;
                }


                await Promise.all([
                    this.loadOptStatuses(),
                    this.loadCampaignTitles(),
                ]);

                this.showHistoryInlineAlert = false;
            }


            this.loading = false;
        },

        async loadMessageHistory() {
            const {
                limit, offset,
            } = this;

            this.loadingMessages = true;

            try {
                const { data } = await axios.get(`/app/contact/${this.contactId}/automatedSmsHistory?limit=${limit}&pageNumber=${offset}`);

                this.loadingMessages = false;
                this.showHistoryInlineAlert = false;
                this.automatedMessageHistory = [
                    ...this.automatedMessageHistory,
                    ...data,
                ];

                this.offset += 1;

                if (data.length < limit) {
                    this.noMoreData = true;
                }
            } catch {
                this.showHistoryInlineAlert = true;
                this.automatedMessageHistory = [];
                this.loadingMessages = false;
            }
        },

        async handleInfiniteScrollLoad() {
            if (this.firstLoad) {
                this.firstLoad = false;

                return;
            }
            await this.loadMessageHistory();
        },

        async loadContactDetails() {
            const { data } = await axios.get(`/app/contact/${this.contactId}`);

            this.contact = data;
        },

        getOptStatus({ toPhoneNumber }) {
            const { statusKind } = this.optStatuses.find(({ phoneNumber }) => normalizePhoneNumber(phoneNumber) === normalizePhoneNumber(toPhoneNumber)) || {};

            return statusKind;
        },

        async loadOptStatuses() {
            const numbers = this.phones.map(({ number }) => normalizePhoneNumber(number));

            const params = {
                fromNumber: this.automatedSmsAccount?.phoneNumber,
                phoneNumbers: numbers.join(),
            };

            const { optInStatuses } = await getRecipientPhoneStatus(params);

            this.optStatuses = optInStatuses;
        },

        async loadAutomatedSmsAccount() {
            try {
                const { data } = await axios.get('/app/automatedSmsFunnel/getAccount');

                if (data) {
                    this.setupComplete = true;
                    this.automatedSmsAccount = data;
                } else {
                    this.showHistoryInlineAlert = true;
                }
            } catch (error) {
                this.showHistoryInlineAlert = true;
            }
        },

        async loadCampaignTitles() {
            const campaignIds = this.automatedMessageHistory
                .filter((item) => item.messageType === ADVANCED_AUTOMATION)
                .map(({ externalReferenceId }) => externalReferenceId);

            if (campaignIds.length > 0) {
                const { data } = await axios.get(`/app/funnel/summary?ids=${campaignIds.join()}`);

                this.campaignSummaries = data;
            }
        },

        getSourceTitle(message) {
            const { messageType, externalReferenceId, parentTitle } = message;

            if (messageType === ADVANCED_AUTOMATION) {
                const data = this.campaignSummaries.find(({ id }) => `${id}` === externalReferenceId) || {};

                return data ? data.name : '';
            }

            if (messageType === BROADCAST) {
                return parentTitle;
            }

            return '';
        },
    },
};
</script>

<style lang="scss" scoped>
.section-header {
    display: flex;
    flex-direction: column;
}

.section-title {
    @include margin-start(px-to-rem(18));
    font-size: px-to-rem(18);
    font-weight: $font-weight-bold;
    padding-bottom: $spacing-050;
}

.phone-number {
    @include margin-start(px-to-rem(18));
    font-size: $font-size-md;
    padding-bottom: $spacing-150;
}

.marketing-messages-header {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
}

.marketing-messages-info {
    display: flex;
    flex-direction: column;
}

.empty-state {
    width: 100%;
    text-align: center;
    margin-top: $spacing-200;
    background-color: $color-gray-50;
}

.empty-state {
    width: 75%;
    position: relative;
    padding: $spacing-200;
    margin: $spacing-200;
    color: $color-ink-700;
    border: none;
    font-style: italic;
    background-color: $color-gray-100;
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
    text-align: left;
}

.error-retry {
    margin-top: $spacing-200;
    margin-bottom: $spacing-200;
}
</style>
